import type { CalendarEvent, EventCalendar as EventCalendarPayload } from './admin/api'
import type { CatalogSport } from './api'
import { useMemo, useState } from 'react'
import { assetUrl } from './api'

// ── Event calendar (embed Overview + sport pages) ────────────────────
// Upcoming events from the AltSportsData API, read-only.
//
// Overview (`variant="overview"`): a week-by-week grid, Monday first, from
// this week through the NEXT 7D / 14D / 30D horizon. A day cell shows ONE
// CHIP PER SPORT (logo · league · ×n), never one row per event, so the
// grid answers "which sports have something on this week" at a glance.
// Multi-day events occupy every day they span. Selecting a day lists
// that day's events underneath; those rows open the sport's embed page.
// (A sports logo filter row and a dot legend sat above the grid on
// 2026-10-21 and were removed the same day at Cole's request.)
//
// Sport page (`variant="sport"`): the original day-grouped list, filtered
// to that sport — one sport needs no grouping.

export const CALENDAR_DAY_OPTIONS = [7, 14, 30] as const

interface Props {
  calendar: EventCalendarPayload | null
  error: string | null
  days: number
  onDays: (days: number) => void
  catalog: CatalogSport[] | null
  onSelect: (slug: string) => void
  variant?: 'overview' | 'sport'
  sportSlug?: string | null
}

interface Row {
  event: CalendarEvent
  /** Identical (league, name, start) rows collapsed — e.g. "October 13 (1 game)" ×6. */
  copies: number
}

interface DayGroup {
  key: string
  label: string
  rows: Row[]
}

/** One league's presence in a day cell. */
interface Chip {
  key: string
  slug: string | null
  name: string
  count: number
  /** Some event of this league is in progress on this day (start ≤ now ≤ end). */
  live: boolean
}

interface Cell {
  key: string
  date: Date
  /** Inside today … today + days − 1. Cells padding the first/last week are not. */
  inWindow: boolean
  isToday: boolean
  chips: Chip[]
  rows: Row[]
}

/** One league's presence across the whole window — feeds the "N SPORTS" count. */
interface SportSummary {
  key: string
  slug: string | null
  name: string
  count: number
  nextStart: number
}

const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const DAY_FMT = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
const LONG_DAY_FMT = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'short' })
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
const SHORT_DATE_FMT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const UPDATED_FMT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

function localDayKey(ms: number): string {
  const d = new Date(ms)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The feed sends date-only events as midnight UTC — show no clock time for those. */
function hasClockTime(iso: string): boolean {
  return !/T00:00:00(\.000)?Z$/.test(iso)
}

/**
 * The local day an event belongs to. Date-only events (midnight UTC) take
 * their ISO date as-is: converting them to local time would file an
 * October 22 race under October 21 for every US viewer.
 */
function eventDayKey(iso: string): string {
  return hasClockTime(iso) ? localDayKey(Date.parse(iso)) : iso.slice(0, 10)
}

function parseDayKey(key: string): Date {
  const [y = 0, m = 1, d = 1] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function startOfLocalDay(ms: number): Date {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d
}

function mondayOf(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7))
}

/** Collapse duplicate rows (same league, name, start) into one with a copy count. */
function dedupe(events: CalendarEvent[]): Row[] {
  const seen = new Map<string, Row>()
  for (const e of events) {
    if (Number.isNaN(Date.parse(e.startDate)))
      continue
    const dupKey = `${e.key}|${e.name}|${e.startDate}`
    const existing = seen.get(dupKey)
    if (existing)
      existing.copies++
    else
      seen.set(dupKey, { event: e, copies: 1 })
  }
  return [...seen.values()]
}

function isInProgress(e: CalendarEvent, nowMs: number): boolean {
  const start = Date.parse(e.startDate)
  const end = e.endDate ? Date.parse(e.endDate) : Number.NaN
  return start <= nowMs && !Number.isNaN(end) && end >= nowMs
}

/** Sport-page list: rows grouped by day, in-progress events filed under today. */
function groupByDay(rows: Row[], horizonMs: number, nowMs: number): DayGroup[] {
  const groups = new Map<string, DayGroup>()
  for (const row of rows) {
    const start = Date.parse(row.event.startDate)
    if (start > horizonMs)
      continue
    const fileUnder = start < nowMs ? nowMs : start
    const key = localDayKey(fileUnder)
    let g = groups.get(key)
    if (!g) {
      g = { key, label: DAY_FMT.format(fileUnder).toUpperCase(), rows: [] }
      groups.set(key, g)
    }
    g.rows.push(row)
  }
  return [...groups.values()].sort((a, b) => a.key.localeCompare(b.key))
}

/**
 * Overview grid: whole weeks (Mon–Sun) from this week through the week
 * holding today + days − 1. Every deduped event lands in each in-window
 * day it spans; a day's chips are one per league, ordered like the legend.
 */
function buildGrid(rows: Row[], nowMs: number, days: number): { weeks: Cell[][], sports: SportSummary[] } {
  const today = startOfLocalDay(nowMs)
  const todayKey = localDayKey(today.getTime())
  const horizonKey = localDayKey(addDays(today, days - 1).getTime())
  const first = mondayOf(today)
  const last = addDays(mondayOf(parseDayKey(horizonKey)), 6)

  const cells = new Map<string, Cell>()
  for (let d = first; d <= last; d = addDays(d, 1)) {
    const key = localDayKey(d.getTime())
    cells.set(key, { key, date: d, inWindow: key >= todayKey && key <= horizonKey, isToday: key === todayKey, chips: [], rows: [] })
  }

  const summary = new Map<string, SportSummary>()
  const placements: { row: Row, keys: string[] }[] = []
  for (const row of rows) {
    const e = row.event
    const startKey = eventDayKey(e.startDate)
    const endRaw = e.endDate && !Number.isNaN(Date.parse(e.endDate)) ? eventDayKey(e.endDate) : startKey
    const endKey = endRaw < startKey ? startKey : endRaw
    if (startKey > horizonKey || endKey < todayKey)
      continue
    const from = startKey < todayKey ? todayKey : startKey
    const to = endKey > horizonKey ? horizonKey : endKey
    const keys: string[] = []
    for (let d = parseDayKey(from), toKey = to; ; d = addDays(d, 1)) {
      const k = localDayKey(d.getTime())
      if (k > toKey)
        break
      keys.push(k)
    }
    placements.push({ row, keys })
    const start = Date.parse(e.startDate)
    const s = summary.get(e.key)
    if (s) {
      s.count++
      s.nextStart = Math.min(s.nextStart, start)
    }
    else {
      summary.set(e.key, { key: e.key, slug: e.slug, name: e.competition, count: 1, nextStart: start })
    }
  }
  const sports = [...summary.values()].sort((a, b) => a.nextStart - b.nextStart || a.name.localeCompare(b.name))
  const order = new Map(sports.map((s, i) => [s.key, i]))

  for (const { row, keys } of placements) {
    const e = row.event
    const live = isInProgress(e, nowMs)
    for (const k of keys) {
      const cell = cells.get(k)
      if (!cell)
        continue
      cell.rows.push(row)
      const chip = cell.chips.find(c => c.key === e.key)
      if (chip) {
        chip.count++
        chip.live ||= live
      }
      else {
        cell.chips.push({ key: e.key, slug: e.slug, name: e.competition, count: 1, live })
      }
    }
  }
  for (const cell of cells.values()) {
    cell.chips.sort((a, b) => (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0))
    cell.rows.sort((a, b) => Date.parse(a.event.startDate) - Date.parse(b.event.startDate) || a.event.name.localeCompare(b.event.name))
  }

  const all = [...cells.values()]
  const weeks: Cell[][] = []
  for (let i = 0; i < all.length; i += 7) weeks.push(all.slice(i, i + 7))
  return { weeks, sports }
}

function coverage(slug: string | null, catalog: CatalogSport[] | null): { cls: string, title: string } {
  if (!slug)
    return { cls: 'is-none', title: 'No catalog row — not embeddable' }
  const sport = catalog?.find(s => s.slug === slug)
  if (!sport)
    return { cls: 'is-none', title: 'Not in the catalog' }
  if (sport.hasData)
    return { cls: 'is-live', title: 'Live odds now' }
  if (sport.configured)
    return { cls: 'is-quiet', title: 'Odds config exists — no odds posted yet' }
  return { cls: 'is-none', title: 'Catalog-only (map / distribution embeds)' }
}

function Mark({ logo, fallback }: { logo: string | null | undefined, fallback: string }) {
  return (
    <span className="ew-cal-mark" aria-hidden="true">
      {logo
        ? <img src={assetUrl(logo)} alt="" loading="lazy" />
        : <span className="ew-cal-mark-txt">{fallback.slice(0, 3).toUpperCase()}</span>}
    </span>
  )
}

interface EventRowProps {
  row: Row
  nowMs: number
  logo: string | null | undefined
  /** Overview rows carry the coverage dot and open the sport; sport-page rows are static. */
  cov: { cls: string, title: string } | null
  onSelect: ((slug: string) => void) | null
  leadWithLeague: boolean
}

function EventRow({ row: { event: e, copies }, nowMs, logo, cov, onSelect, leadWithLeague }: EventRowProps) {
  const start = Date.parse(e.startDate)
  const end = e.endDate ? Date.parse(e.endDate) : Number.NaN
  const inProgress = isInProgress(e, nowMs)
  const multiDay = !Number.isNaN(end) && eventDayKey(e.endDate as string) !== eventDayKey(e.startDate)
  const place = e.locationGroup ?? e.location
  // On a sport page the league name is the page title — lead with the
  // tour / location instead.
  const meta = (leadWithLeague
    ? [e.competition, e.tour && e.tour !== e.name ? e.tour : null, place]
    : [e.tour && e.tour !== e.name ? e.tour : null, e.location && e.location !== place ? e.location : null, place]).filter(Boolean).join(' · ')
  const body = (
    <>
      <span className="ew-cal-time b-mono">
        {inProgress ? 'LIVE' : hasClockTime(e.startDate) ? TIME_FMT.format(start) : '—'}
      </span>
      <Mark logo={logo} fallback={e.key} />
      <span className="ew-cal-body">
        <span className="ew-cal-name">
          {e.name}
          {copies > 1 && <span className="ew-cal-copies b-mono">{` ×${copies}`}</span>}
          {multiDay && <span className="ew-cal-until b-mono-dim">{` → ${SHORT_DATE_FMT.format(end)}`}</span>}
        </span>
        <span className="ew-cal-meta b-mono-dim">{meta}</span>
      </span>
      {cov && <i className={`ew-cal-dot ${cov.cls}`} title={cov.title} />}
    </>
  )
  if (!onSelect)
    return <div className="ew-cal-row is-static">{body}</div>
  return (
    <button
      type="button"
      className="ew-cal-row"
      disabled={!e.slug}
      onClick={e.slug ? () => onSelect(e.slug as string) : undefined}
      title={e.slug ? `Open ${e.competition}` : e.competition}
    >
      {body}
    </button>
  )
}

function Foot({ calendar }: { calendar: EventCalendarPayload | null }) {
  return (
    <div className="ew-cal-foot b-mono-dim b-tiny">
      {calendar
        ? `VIA ALTSPORTSDATA API · UPDATED ${UPDATED_FMT.format(Date.parse(calendar.fetchedAt))}${calendar.stale ? ' · REFRESHING' : ''}${calendar.failed.length ? ` · ${calendar.failed.length} LEAGUE${calendar.failed.length === 1 ? '' : 'S'} FAILED` : ''}`
        : 'VIA ALTSPORTSDATA API'}
    </div>
  )
}

function WindowChips({ days, onDays }: { days: number, onDays: (d: number) => void }) {
  return (
    <div className="ew-cal-chips" role="group" aria-label="Window">
      {CALENDAR_DAY_OPTIONS.map(d => (
        <button
          key={d}
          type="button"
          className={`b-chip${days === d ? ' is-active' : ''}`}
          onClick={() => onDays(d)}
        >
          <span className="b-chip-name">{`NEXT ${d}D`}</span>
        </button>
      ))}
    </div>
  )
}

export default function EventCalendar({ calendar, error, days, onDays, catalog, onSelect, variant = 'overview', sportSlug = null }: Props) {
  const isSport = variant === 'sport'
  // "Now" is pinned per payload/window so the day grouping doesn't shift
  // between renders; a fresh fetch re-anchors it.
  const nowMs = useMemo(() => Date.now(), [calendar, days])
  const rows = useMemo(
    () => !calendar ? [] : dedupe(isSport ? calendar.events.filter(e => e.slug === sportSlug) : calendar.events),
    [calendar, isSport, sportSlug],
  )
  const logoBySlug = useMemo(() => {
    const m = new Map<string, string | null>()
    for (const s of catalog ?? []) m.set(s.slug, s.logo_url)
    return m
  }, [catalog])
  const logoFor = (slug: string | null) => (slug ? logoBySlug.get(slug) : null)

  // ── Sport page: the day-grouped list ──────────────────────────────
  if (isSport) {
    const groups = calendar ? groupByDay(rows, nowMs + days * 864e5, nowMs) : []
    // A sport with no AltSportsData league at all (NASCAR, MotoGP, cycling…)
    // reads differently from one that is covered but idle right now.
    const inCalendar = Boolean(calendar?.leagues.some(l => l.slug === sportSlug))
    const total = groups.reduce((n, g) => n + g.rows.length, 0)
    return (
      <aside className="ew-cal is-sport" aria-label="Upcoming events">
        <div className="ew-cal-head">
          <div className="ew-cal-title-row">
            <span className="ew-cal-title">UPCOMING EVENTS</span>
            <span className="b-mono-dim b-tiny">{calendar ? `${total} EVENT${total === 1 ? '' : 'S'}` : ''}</span>
          </div>
          <WindowChips days={days} onDays={onDays} />
        </div>
        <div className="ew-cal-list">
          {error && <p className="ew-cal-note b-mono-dim">{`CALENDAR UNAVAILABLE — ${error}`}</p>}
          {!error && !calendar && <p className="ew-cal-note b-mono-dim">LOADING…</p>}
          {calendar && !inCalendar && <p className="ew-cal-note b-mono-dim">NOT IN THE ALTSPORTSDATA CALENDAR</p>}
          {calendar && inCalendar && total === 0 && <p className="ew-cal-note b-mono-dim">{`NO EVENTS IN THE NEXT ${days} DAYS`}</p>}
          {groups.map(g => (
            <section key={g.key} className="ew-cal-day">
              <h4 className="ew-cal-day-label">{g.label}</h4>
              {g.rows.map(row => (
                <EventRow key={row.event.id} row={row} nowMs={nowMs} logo={logoFor(row.event.slug)} cov={null} onSelect={null} leadWithLeague={false} />
              ))}
            </section>
          ))}
        </div>
        <Foot calendar={calendar} />
      </aside>
    )
  }

  // ── Overview: the grid ────────────────────────────────────────────
  return <OverviewGrid calendar={calendar} error={error} days={days} onDays={onDays} catalog={catalog} onSelect={onSelect} rows={rows} nowMs={nowMs} logoFor={logoFor} />
}

interface GridProps extends Omit<Props, 'variant' | 'sportSlug'> {
  rows: Row[]
  nowMs: number
  logoFor: (slug: string | null) => string | null | undefined
}

function OverviewGrid({ calendar, error, days, onDays, catalog, onSelect, rows, nowMs, logoFor }: GridProps) {
  // The selected day is local state: a fresh payload keeps it, and a
  // selection outside the new grid falls back to today.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const { weeks, sports } = useMemo(() => buildGrid(rows, nowMs, days), [rows, nowMs, days])
  const cells = useMemo(() => weeks.flat(), [weeks])
  const todayKey = localDayKey(nowMs)
  const selected = cells.find(c => c.key === selectedKey) ?? cells.find(c => c.key === todayKey) ?? null

  return (
    <section className="ew-cal is-grid" aria-label="Event calendar">
      <div className="ew-cal-head">
        {/* Title left, the window chips right (Cole: the range/count
            readout "replace this with the calendar length icons"). */}
        <div className="ew-cal-title-row">
          <span className="ew-cal-title">EVENT CALENDAR</span>
          <WindowChips days={days} onDays={onDays} />
        </div>
        {error && <p className="ew-cal-note b-mono-dim">{`CALENDAR UNAVAILABLE — ${error}`}</p>}
        {!error && !calendar && <p className="ew-cal-note b-mono-dim">LOADING…</p>}
        {calendar && sports.length === 0 && <p className="ew-cal-note b-mono-dim">{`NO EVENTS IN THE NEXT ${days} DAYS`}</p>}
      </div>

      {calendar && (
        <div className="ew-calgrid" role="grid" aria-label="Weeks">
          <div className="ew-calgrid-dow" role="row">
            {DOW.map(d => <span key={d} role="columnheader" className="b-mono-dim b-tiny">{d}</span>)}
          </div>
          {weeks.map(week => (
            <div key={week[0]?.key ?? ''} className="ew-calgrid-week" role="row">
              {week.map((cell) => {
                const showMonth = cell.date.getDate() === 1 || cell === cells[0]
                const label = `${showMonth ? `${MONTH_FMT.format(cell.date).toUpperCase()} ` : ''}${cell.date.getDate()}`
                const n = cell.rows.length
                const isSelected = selected?.key === cell.key
                return (
                  <button
                    key={cell.key}
                    type="button"
                    role="gridcell"
                    className={`ew-calgrid-cell${cell.inWindow ? '' : ' is-out'}${cell.isToday ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}`}
                    disabled={!cell.inWindow}
                    aria-pressed={isSelected}
                    aria-label={`${LONG_DAY_FMT.format(cell.date)} — ${n} event${n === 1 ? '' : 's'}`}
                    onClick={() => setSelectedKey(cell.key)}
                  >
                    <span className="ew-calgrid-date b-mono">{label}</span>
                    {cell.inWindow && cell.chips.map(chip => (
                      <span
                        key={chip.key}
                        className={`ew-calgrid-chip${chip.live ? ' is-now' : ''}`}
                        title={`${chip.name} · ${chip.count} event${chip.count === 1 ? '' : 's'}${chip.live ? ' · in progress' : ''}`}
                      >
                        <Mark logo={logoFor(chip.slug)} fallback={chip.key} />
                        <span className="ew-calgrid-chip-name">{chip.name}</span>
                        {chip.count > 1 && <span className="ew-calgrid-chip-n b-mono">{`×${chip.count}`}</span>}
                      </span>
                    ))}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}

      {calendar && selected && (
        <div className="ew-cal-detail" aria-live="polite">
          <h4 className="ew-cal-day-label">
            {LONG_DAY_FMT.format(selected.date).toUpperCase()}
            <span className="ew-cal-day-count">{` · ${selected.rows.length} EVENT${selected.rows.length === 1 ? '' : 'S'}`}</span>
          </h4>
          {selected.rows.length === 0 && <p className="ew-cal-note b-mono-dim">NO EVENTS</p>}
          {selected.rows.map(row => (
            <EventRow
              key={row.event.id}
              row={row}
              nowMs={nowMs}
              logo={logoFor(row.event.slug)}
              cov={coverage(row.event.slug, catalog)}
              onSelect={onSelect}
              leadWithLeague
            />
          ))}
        </div>
      )}

      <Foot calendar={calendar} />
    </section>
  )
}
